import { GridApi } from '@ag-grid-community/core';
import { Connect } from '@genesislcap/foundation-comms';
import Papa from 'papaparse';
import { NUMERIC_BOND_ID_REGEX } from '../../../../mbm/utils/bulk-response-upload/upload-validator';
import { showBulkToast, showBackendUnavailableToast } from './bond-bidding-bulk-utils';
import {
  BOND_BIDDING_MESSAGES,
  BOND_BIDDING_TOAST_TITLE,
} from './bond-bidding-messages';
import {
  validateUploadRows,
  formatBidPriceForUpdate,
  type UploadValidationResult,
  RFQ_STATUS_FIRM_UNTIL_ERROR,
  normalizeBidPriceType,
} from '../../../../mbm/utils/bulk-response-upload/upload-validator';
import { countEditedBackendControlledResponseType } from '../../../../mbm/utils/bond-bid-utils';

export interface ParsedUploadRow {
  rowIndex: number;
  BOND_ID: string;
  BID_PRICE: string;
  BID_PRICE_TYPE: string;
}

export interface CsvParseResult {
  success: boolean;
  error?: string;
  headers?: string[];
  rows: ParsedUploadRow[];
}

export interface ValidateAgainstMergedRowsResult {
  result: UploadValidationResult;
  backendRowMap: Map<string, Record<string, unknown>>;
}

const UPLOAD_COLUMN_ALIASES: Record<string, string> = {
  'reference#': 'BOND_ID',
  'bond_id': 'BOND_ID',
  'reference': 'BOND_ID',
  'bond id': 'BOND_ID',
  'response': 'BID_PRICE',
  'bid_price': 'BID_PRICE',
  'response type': 'BID_PRICE_TYPE',
  'bid_price_type': 'BID_PRICE_TYPE',
};

const REQUIRED_FIELDS = ['BOND_ID', 'BID_PRICE', 'BID_PRICE_TYPE'] as const;
const MAX_UPLOAD_ROWS = 2000;

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapHeaderToField(header: string): string | null {
  const normalized = normalizeHeader(header);
  return UPLOAD_COLUMN_ALIASES[normalized] ?? (REQUIRED_FIELDS.includes(normalized as any) ? normalized : null);
}

function stripBom(text: string): string {
  if (text.charCodeAt(0) === 0xfeff) {
    return text.slice(1);
  }
  return text;
}

function stripLeadingTab(value: string): string {
  return value.startsWith('\t') ? value.slice(1) : value;
}

/**
 * Parse CSV text using PapaParse and extract the 3 mandatory columns.
 * Exported for unit testing.
 */
export function parseBulkResponseCsv(csvText: string): CsvParseResult {
  const text = stripBom(csvText.trim());
  if (!text) {
    return { success: false, error: 'CSV is empty.', rows: [] };
  }

  const parseResult = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: true,
    delimiter: '',
    newline: '',
    quoteChar: '"',
    escapeChar: '"',
  });

  if (parseResult.errors.length > 0) {
    const errorMessages = parseResult.errors.map((e) => e.message).join('; ');
    return {
      success: false,
      error: `CSV parsing errors: ${errorMessages}`,
      rows: [],
    };
  }

  const data = parseResult.data;
  if (!data || data.length < 2) {
    return {
      success: false,
      error: 'CSV must have a header row and at least one data row.',
      rows: [],
    };
  }

  const headerRow = data[0];
  const columnToField: Record<number, string> = {};

  for (let i = 0; i < headerRow.length; i++) {
    const field = mapHeaderToField(headerRow[i] || '');
    if (field && !Object.values(columnToField).includes(field)) {
      columnToField[i] = field;
    }
  }

  const missing: string[] = [];
  for (const f of REQUIRED_FIELDS) {
    if (!Object.values(columnToField).includes(f)) {
      missing.push(
        f === 'BOND_ID' ? 'Reference#/BOND_ID' : f === 'BID_PRICE' ? 'Response' : 'Response Type'
      );
    }
  }
  if (missing.length > 0) {
    return {
      success: false,
      error: `Missing required column(s): ${missing.join(', ')}`,
      headers: headerRow,
      rows: [],
    };
  }

  const rows: ParsedUploadRow[] = [];
  for (let r = 1; r < data.length; r++) {
    const cells = data[r];
    const row: Record<string, string> = {};
    for (const [colIdx, field] of Object.entries(columnToField)) {
      const idx = parseInt(colIdx, 10);
      let val = (cells[idx] ?? '').trim();
      if (field === 'BOND_ID') {
        val = stripLeadingTab(val);
      }
      row[field] = val;
    }

    const bondId = row['BOND_ID'] ?? '';
    const bidPrice = row['BID_PRICE'] ?? '';
    const bidPriceType = row['BID_PRICE_TYPE'] ?? '';

    // Treat rows where all key fields are blank as deleted/no-op rows and skip them.
    const isCompletelyBlank =
      !bondId.trim() && !bidPrice.trim() && !bidPriceType.trim();
    if (isCompletelyBlank) {
      continue;
    }

    rows.push({
      rowIndex: r + 1,
      BOND_ID: bondId,
      BID_PRICE: bidPrice,
      BID_PRICE_TYPE: bidPriceType,
    });
  }

  if (rows.length > MAX_UPLOAD_ROWS) {
    return {
      success: false,
      error: `CSV has too many data rows (${rows.length}). Maximum allowed is ${MAX_UPLOAD_ROWS}.`,
      headers: headerRow,
      rows: [],
    };
  }

  return { success: true, headers: headerRow, rows };
}

export async function parseUploadFile(file: File): Promise<CsvParseResult | null> {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadFailed,
      BOND_BIDDING_MESSAGES.upload.notCsv,
      'error'
    );
    return null;
  }

  const text = await file.text();
  const parseResult = parseBulkResponseCsv(text);

  if (!parseResult.success) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadFailed,
      parseResult.error ?? BOND_BIDDING_MESSAGES.upload.parseFailedFallback,
      'error'
    );
    return null;
  }
  if (!parseResult.rows?.length) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadFailed,
      BOND_BIDDING_MESSAGES.upload.emptyCsv,
      'error'
    );
    return null;
  }

  return parseResult;
}

export function validateAgainstMergedRows(
  parsedRows: ParsedUploadRow[],
  mergedRows: any[]
): ValidateAgainstMergedRowsResult | null {
  const bondIdSet = new Set(
    parsedRows.map((r) => r.BOND_ID.trim()).filter(Boolean)
  );

  const backendRowMap = new Map<string, Record<string, unknown>>();
  for (const row of mergedRows) {
    const bondId = String(row?.BOND_ID ?? '');
    if (bondIdSet.has(bondId)) {
      backendRowMap.set(bondId, row);
    }
  }

  const getRowData = (id: string) => {
    const row = backendRowMap.get(id);
    return row ? { data: row } : undefined;
  };

  const result = validateUploadRows(parsedRows, getRowData);

  // Show a warning when the upload contains edits for backend-controlled Response Type rows.
  const editedBackendControlledCount = countEditedBackendControlledResponseType(
    parsedRows,
    backendRowMap
  );
  if (editedBackendControlledCount > 0) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadWarning,
      BOND_BIDDING_MESSAGES.upload.backendControlledResponseType,
      'warning',
      { autoClose: false }
    );
  }

  if (result.validRows === 0) {
    const allFirmUntil =
      result.errorRows > 0 &&
      result.ignoredRows === 0 &&
      result.rows.every(
        (r) =>
          r.hasValidationError &&
          r.existsInGrid &&
          r.errorMessage === RFQ_STATUS_FIRM_UNTIL_ERROR
      );

    const body = allFirmUntil
      ? BOND_BIDDING_MESSAGES.upload.validationAllFirmUntil(result.errorRows)
      : result.errorRows > 0
        ? BOND_BIDDING_MESSAGES.upload.validationNoValidWithErrors(
            result.errorRows,
            result.ignoredRows
          )
        : BOND_BIDDING_MESSAGES.upload.validationNoValidAllIgnored(result.ignoredRows);

    showBulkToast(BOND_BIDDING_TOAST_TITLE.uploadFailed, body, 'error');
    return null;
  }

  return { result, backendRowMap };
}

export function applyToGrid(
  result: UploadValidationResult,
  backendRowMap: Map<string, Record<string, unknown>>,
  gridApi: GridApi | null,
  activityMap: Map<string, any>,
  bondDetailsOpen: boolean,
  onResetFiltersAndApplyBondIdFilter: (bondIds: string[]) => void,
  selectedBondId?: string,
  onUpdateTrancheDetail?: (row: Record<string, unknown>) => void,
): void {
  if (!result?.rows?.length) return;

  const validRows = result.rows.filter((r) => r.existsInGrid && !r.hasValidationError);
  if (!validRows.length) return;

  const updatedRows: Record<string, unknown>[] = [];
  for (const row of validRows) {
    const backendRow = backendRowMap.get(row.BOND_ID);
    if (!backendRow) continue;

    const bidPrice = formatBidPriceForUpdate(row.BID_PRICE, row.BID_PRICE_TYPE);
    const bidPriceType = normalizeBidPriceType(row.BID_PRICE_TYPE);

    const merged = { ...backendRow } as Record<string, unknown>;
    merged.BID_PRICE = bidPrice;
    merged.BID_PRICE_TYPE = bidPriceType;
    const hasExistingOrder =
      backendRow.BID_ID && (backendRow.STATUS as string) === 'DUE_IN';
    if (hasExistingOrder) {
      merged._originalBidPrice = backendRow.BID_PRICE ?? bidPrice;
      merged._originalBidPriceType =
        (backendRow.BID_PRICE_TYPE as string) ?? bidPriceType;
    } else {
      merged._originalBidPrice = bidPrice;
      merged._originalBidPriceType = bidPriceType;
    }
    updatedRows.push(merged);
    activityMap.set(row.BOND_ID, merged);
  }

  if (gridApi) {
    const rowsInGrid = updatedRows.filter((r) => gridApi.getRowNode(r.BOND_ID as string)?.data);
    if (rowsInGrid.length > 0) {
      gridApi.applyTransaction({ update: rowsInGrid });
      const rowNodes = rowsInGrid
        .map((r) => gridApi.getRowNode(r.BOND_ID as string))
        .filter(Boolean);
      gridApi.redrawRows({ rowNodes });
    }
  }

  if (bondDetailsOpen && selectedBondId && onUpdateTrancheDetail) {
    const selectedUpdate = updatedRows.find((r) => r.BOND_ID === selectedBondId);
    if (selectedUpdate) {
      onUpdateTrancheDetail(selectedUpdate);
    }
  }

  onResetFiltersAndApplyBondIdFilter(validRows.map((r) => r.BOND_ID));

  const msg =
    result.ignoredRows > 0 || result.errorRows > 0
      ? BOND_BIDDING_MESSAGES.upload.completePartial(
          validRows.length,
          result.ignoredRows,
          result.errorRows
        )
      : BOND_BIDDING_MESSAGES.upload.completeSuccess(validRows.length);
  showBulkToast(BOND_BIDDING_TOAST_TITLE.uploadComplete, msg, 'success');
}

/**
 * Validates uploaded Reference# values (numeric only). Shows toasts when needed.
 * Returns null when upload should not proceed.
 */
export function prepareUploadBondIds(bondIds: string[] | null | undefined): string[] | null {
  const safeBondIds = (bondIds || [])
    .map((id) => String(id).trim())
    .filter((id) => id.length > 0 && NUMERIC_BOND_ID_REGEX.test(id));

  if (safeBondIds.length === 0) {
    if ((bondIds || []).length > 0) {
      showBulkToast(
        BOND_BIDDING_TOAST_TITLE.uploadFailed,
        BOND_BIDDING_MESSAGES.upload.noValidReferenceIds,
        'error'
      );
    }
    return null;
  }

  if (safeBondIds.length < (bondIds || []).length) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadWarning,
      BOND_BIDDING_MESSAGES.upload.someReferenceIdsIgnored,
      'warning'
    );
  }

  return safeBondIds;
}

/**
 * Registers the uploaded bond list on the server and returns the batch id used
 * to filter the grid in upload mode. Returns null on failure (toast already shown).
 */
export async function registerUploadBondFilter(
  connect: Connect,
  bondIds: string[]
): Promise<string | null> {
  try {
    const reply = await connect.commitEvent('EVENT_UPLOAD_BOND_FILTER', {
      DETAILS: {
        BOND_IDS: bondIds,
      },
    });

    if (reply?.MESSAGE_TYPE !== 'EVENT_ACK') {
      const errorRows = Array.isArray((reply as { ERROR?: unknown })?.ERROR)
        ? (reply as { ERROR?: Array<{ TEXT?: string }> }).ERROR
        : [];
      const errorText =
        errorRows
          ?.map((row) => row?.TEXT)
          .filter((text): text is string => typeof text === 'string' && text.length > 0)
          .join('; ') || BOND_BIDDING_MESSAGES.upload.filterEventFailedFallback;
      showBulkToast(BOND_BIDDING_TOAST_TITLE.uploadFailed, errorText, 'error');
      return null;
    }

    const generated = (reply as { GENERATED?: unknown[] })?.GENERATED;
    const first = Array.isArray(generated) ? generated[0] : generated;
    const payload =
      first && typeof first === 'object' ? (first as Record<string, unknown>) : {};
    const id = payload.CORRELATION_ID;
    if (typeof id === 'string' && id.trim().length > 0) {
      return id.trim();
    }

    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.uploadFailed,
      BOND_BIDDING_MESSAGES.upload.noCorrelationId,
      'error'
    );
    return null;
  } catch {
    showBackendUnavailableToast('upload', BOND_BIDDING_MESSAGES.upload.registerBatchFailed);
    return null;
  }
}

/** Numeric Reference# values from visible grid rows (after filters/sort). */
export function getVisibleNumericBondIds(gridApi: GridApi | null | undefined): string[] {
  if (!gridApi) return [];
  const ids: string[] = [];
  (gridApi as { forEachNodeAfterFilterAndSort?: (callback: (node: { data?: { BOND_ID?: string } }) => void) => void })
    .forEachNodeAfterFilterAndSort?.((node) => {
    const bondId = String(node?.data?.BOND_ID ?? '').trim();
    if (bondId && NUMERIC_BOND_ID_REGEX.test(bondId)) {
      ids.push(bondId);
    }
  });
  return ids;
}

/** Comma-separated Reference# list for Submit All in upload mode. */
export function getUploadModeReferenceIdSource(
  gridApi: GridApi | null | undefined
): string {
  const ids = getVisibleNumericBondIds(gridApi);
  return ids.length > 0 ? ids.join(', ') : '';
}

